import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      localStorage.removeItem('adminData');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Initialize data
export const initData = () => api.get('/init');

// Auth APIs
export const verifyEmail = (data) => api.post('/auth/verify-email', data);
export const resendVerification = (data) => api.post('/auth/resend-verification', data);

// User APIs
export const getUserDashboard = () => api.get('/users/dashboard');
export const updateProfile = (data) => api.put('/users/profile', data);
export const getNotifications = () => api.get('/users/notifications');
export const markNotificationRead = (id) => api.put(`/users/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/users/notifications/read-all');

// Deposit APIs
export const createDeposit = (data) => api.post('/deposits/', data);
export const getDeposits = () => api.get('/deposits/');
export const uploadProof = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/deposits/upload-proof', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Withdrawal APIs
export const createWithdrawal = (data) => api.post('/withdrawals/', data);
export const getWithdrawals = () => api.get('/withdrawals/');

// Investment APIs
export const createInvestment = (data) => api.post('/investments/', data);
export const getInvestments = () => api.get('/investments/');
export const withdrawFromInvestment = (id, amount) => api.post(`/investments/${id}/withdraw?amount=${amount}`);

// Transaction APIs
export const getTransactions = (limit = 50) => api.get('/transactions/', { params: { limit } });

// Settings APIs
export const getWalletAddresses = () => api.get('/settings/wallets');
export const getPlatformSettings = () => api.get('/settings/platform');
export const getCryptoPrices = () => api.get('/settings/crypto-prices');
export const getActivityFeed = () => api.get('/settings/activity-feed');

// Admin APIs
export const getAdminDashboard = () => api.get('/admin/dashboard');
export const getAllUsers = (params) => api.get('/admin/users', { params });
export const getPendingUsers = () => api.get('/admin/users/pending');
export const getUserDetail = (id) => api.get(`/admin/users/${id}`);
export const updateUser = (id, data) => api.put(`/admin/users/${id}`, data);
export const approveUser = (id) => api.post(`/admin/users/${id}/approve`);
export const rejectUser = (id) => api.post(`/admin/users/${id}/reject`);
export const suspendUser = (id) => api.post(`/admin/users/${id}/suspend`);
export const banUser = (id) => api.post(`/admin/users/${id}/ban`);
export const deleteUser = (id) => api.delete(`/admin/users/${id}`);
export const getAuditLog = (params) => api.get('/admin/audit-log', { params });
export const adjustBalance = (userId, data) => api.post(`/admin/users/${userId}/balance`, data);

export const getAllDeposits = (status) => api.get('/admin/deposits', { params: { status } });
export const updateDeposit = (id, data) => api.put(`/admin/deposits/${id}`, data);
export const manualDeposit = (userId, amount, cryptoType) => 
  api.post(`/admin/deposits/manual?user_id=${userId}&amount=${amount}&crypto_type=${cryptoType}`);

export const getAllWithdrawals = (status) => api.get('/admin/withdrawals', { params: { status } });
export const updateWithdrawal = (id, data) => api.put(`/admin/withdrawals/${id}`, data);

export const addProfit = (data) => api.post('/admin/profits/add', data);
export const addLoss = (data) => api.post('/admin/profits/add-loss', data);
export const distributeDailyProfits = () => api.post('/admin/profits/distribute-daily');
export const getProfitDistributions = (limit = 30) => api.get('/admin/profits/distributions', { params: { limit } });
export const simulateProfits = () => api.post('/admin/profits/simulate');

export const getSettings = () => api.get('/admin/settings');
export const updateSetting = (data) => api.put('/admin/settings', data);

export const sendNotification = (data) => api.post('/admin/notifications', data);

export default api;
